import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { typeColor } from '../lib/format.js';
import ExemplarKonfig from './ExemplarKonfig.jsx';

const FILTER = [
  { key: 'all', label: 'Alle' },
  { key: 'shiny', label: 'Shiny im Dex' },
  { key: 'shadow', label: 'Crypto' },
  { key: 'lucky', label: 'Glücks' },
  { key: 'favorite', label: 'Favoriten' },
  { key: 'dynamax', label: 'Dynamax' },
  { key: 'note', label: 'Mit Notiz' },
];

/** Grobe Einordnung des IV-Durchschnitts, damit die Farbe etwas aussagt. */
const ivStufe = (p) =>
  p === null ? 'D' : p >= 98 ? 'S' : p >= 90 ? 'A' : p >= 80 ? 'B' : p >= 66 ? 'C' : 'D';

/**
 * Die Sammlung als Kartenübersicht.
 *
 * Zum Anschauen, nicht zum Einstellen — deshalb sitzt alles Bearbeitbare
 * hinter einem Knopf pro Karte und nicht offen auf der Seite.
 *
 * Der Shiny-Status wird hier nur angezeigt, nicht gesetzt: er hängt an der
 * Art und wird im Shiny-Dex gepflegt.
 */
export default function CollectionGrid({ neuLaden }) {
  const [eintraege, setEintraege] = useState(null);
  const [suche, setSuche] = useState('');
  const [filter, setFilter] = useState('all');
  const [fehler, setFehler] = useState(null);

  const laden = (q = '') =>
    api.collection.entries(q)
      .then((d) => setEintraege(d.entries))
      .catch((e) => setFehler(e.message));

  useEffect(() => { laden(suche); }, [neuLaden]);

  // Kurze Verzögerung, damit nicht jeder Tastendruck eine Anfrage auslöst.
  useEffect(() => {
    const id = setTimeout(() => laden(suche), 300);
    return () => clearTimeout(id);
  }, [suche]);

  const sichtbar = useMemo(() => (eintraege ?? []).filter((e) => {
    switch (filter) {
      case 'shiny':    return e.isShiny;
      case 'shadow':   return e.isShadow || e.isPurified;
      case 'lucky':    return e.isLucky;
      case 'favorite': return e.isFavorite;
      case 'dynamax':  return e.isDynamax || e.isGigamax;
      case 'note':     return Boolean(e.note);
      default:         return true;
    }
  }), [eintraege, filter]);

  const aktualisieren = (id, felder) =>
    setEintraege((liste) => liste.map((e) => (e.id === id ? { ...e, ...felder } : e)));

  // Entferntes sofort aus der Liste nehmen, statt neu zu laden: Bei großen
  // Sammlungen wäre ein vollständiger Neuaufbau für eine Zeile unverhältnismäßig.
  const entfernen = (id) =>
    setEintraege((liste) => liste.filter((e) => e.id !== id));

  return (
    <>
      <div className="filters">
        {FILTER.map((f) => (
          <button
            key={f.key}
            type="button"
            className={filter === f.key ? 'chip chip--on' : 'chip'}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        <input
          className="input filters__suche"
          type="search"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Name suchen…"
          aria-label="Sammlung durchsuchen"
        />
      </div>

      {fehler && <div className="error">{fehler}</div>}

      {!eintraege && (
        <div className="grid">
          {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ height: 140 }} />)}
        </div>
      )}

      {eintraege && sichtbar.length === 0 && (
        <div className="empty">
          <div className="empty__icon">✧</div>
          <p><strong>{eintraege.length === 0 ? 'Noch nichts importiert.' : 'Nichts gefunden.'}</strong></p>
          <p>
            {eintraege.length === 0
              ? 'Lade oben deinen CSV-Export hoch, dann steht deine Sammlung hier.'
              : 'Andere Filter oder einen anderen Suchbegriff probieren.'}
          </p>
        </div>
      )}

      {sichtbar.length > 0 && (
        <div className="grid">
          {sichtbar.map((e) => (
            <SammlungsKarte
              key={e.id}
              eintrag={e}
              onGeaendert={aktualisieren}
              onEntfernt={entfernen}
            />
          ))}
        </div>
      )}
    </>
  );
}

function SammlungsKarte({ eintrag: e, onGeaendert, onEntfernt }) {
  const [offen, setOffen] = useState(false);

  const stufe = ivStufe(e.ivAverage);
  const klasse = e.isShiny ? 'card sammel-karte sammel-karte--shiny' : 'card sammel-karte';

  return (
    <article className={klasse}>
      <div className="card__top">
        <div>
          <div className="card__title">
            {e.name}
            {e.form && <span className="mon-card__form"> {e.form}</span>}
            {e.gender && <span className="text-faint"> {e.gender}</span>}
          </div>
          <div className="mon__dex">#{String(e.dexNo).padStart(3, '0')}</div>
        </div>
        <div className="sammel-karte__wp">
          <span className="sammel-karte__wp-zahl">{e.cp}</span>
          <span className="sammel-karte__wp-label">WP</span>
        </div>
      </div>

      <div className="inline">
        {e.ivAverage !== null && (
          <span className={'tier tier--' + stufe}>{e.ivAverage}%</span>
        )}
        {e.ivAttack !== null && (
          <span className="badge badge--soft">{e.ivAttack}/{e.ivDefense}/{e.ivStamina}</span>
        )}
        {e.level !== null && <span className="badge badge--soft">Lvl {e.level}</span>}
      </div>

      {e.types?.length > 0 && (
        <div className="inline">
          {e.types.map((t) => (
            <span className="type" key={t} style={{ '--type-color': typeColor(t) }}>{t}</span>
          ))}
        </div>
      )}

      <div className="inline">
        {e.isShiny && <span className="badge badge--shiny">✦ Schillernd</span>}
        {e.isGigamax && <span className="badge badge--soft">Gigadynamax</span>}
        {e.isDynamax && !e.isGigamax && <span className="badge badge--soft">Dynamax</span>}
        {e.isShadow && <span className="badge badge--soft">Crypto</span>}
        {e.isPurified && <span className="badge badge--soft">Erlöst</span>}
        {e.isLucky && <span className="badge badge--soft">Glücks</span>}
        {e.isFavorite && <span className="badge badge--soft">★</span>}
      </div>

      {e.note && !offen && <p className="card__summary">{e.note}</p>}

      {offen ? (
        <ExemplarKonfig
          eintrag={e}
          onGeaendert={onGeaendert}
          onEntfernt={onEntfernt}
          onSchliessen={() => setOffen(false)}
        />
      ) : (
        <div className="sammel-karte__fuss">
          {e.catalogSlug
            ? <Link to={'/pokemon/' + e.catalogSlug} className="badge badge--soft">Zur Bewertung</Link>
            : <span className="badge badge--soft text-faint">noch nicht bewertet</span>}
          <button className="btn btn--ghost btn--klein" onClick={() => setOffen(true)}>
            Angaben pflegen
          </button>
        </div>
      )}
    </article>
  );
}
